import React, { FC, PropsWithChildren, Suspense } from 'react'
import { isServer } from 'lib'
import { SuspenseErrorBoundary } from './internals/SuspenseErrorBoundary'
import { SuspenseNullState } from 'components/patterns/suspense/errors'
import { useHasMounted } from 'lib/renderWrappers/ClientOnly'
import { DataWrapper } from 'components/patterns/suspense/internals/types'

export interface ResourceProps<T> {
	data?: T
}

interface SuspensfulComponentProps<T> {
	resource: DataWrapper<T>
	Component: FC<React.PropsWithChildren<ResourceProps<T>>>
	errorState?: () => JSX.Element
}

const SuspensefulComponent = <T,>({ resource, Component }: SuspensfulComponentProps<T>): JSX.Element => {
	try {
		const value = resource.read()
		return <Component data={value} />
	} catch (value) {
		//TODO: Either make this configurable to allow dynamic fallbacks, or bake it into the boundary 'somehow'
		if (value instanceof SuspenseNullState) {
			console.log('returning empty')
			return <></>
		} else {
			throw value
		}
	}
}

const EmptyState = () => <></>

/**
 * Use this to create a version of a component which is compatible with DataWrappers
 * @param resource the Data Wrapper Entry
 * @param Component The component
 * @param errorState a fallback error state
 * @constructor
 */
export const SuspensefulComponentWrapper = <T,>({
	resource,
	Component,
	errorState
}: SuspensfulComponentProps<T>): JSX.Element => {
	if (isServer()) {
		return <Component />
	}

	return (
		<SuspenseErrorBoundary fallback={errorState ? errorState() : EmptyState()}>
			<Suspense fallback={<Component />}>
				<SuspensefulComponent Component={Component} resource={resource} />
			</Suspense>
		</SuspenseErrorBoundary>
	)
}

interface SuspenseLoadingProps {
	fallback: JSX.Element
	errorState?: () => JSX.Element
}

export const LoadingBoundary = ({
	children,
	fallback,
	errorState
}: PropsWithChildren<SuspenseLoadingProps>): JSX.Element => {
	const mounted = useHasMounted()
	if (mounted) {
		return (
			<SuspenseErrorBoundary fallback={errorState ? errorState() : EmptyState()}>
				<Suspense fallback={fallback}>{children}</Suspense>
			</SuspenseErrorBoundary>
		)
	} else {
		return fallback
	}
}
